import React from "react"
import { useTranslation } from "react-i18next"
import i18next from "i18next"

import * as FileUtil from "utils/file"
import { Markdown } from "components/Elements/Markdown"

import { useAlert } from "hooks/useAlert"
import { useModal } from "hooks/useModal"

import { UploadIcon, DownloadIcon } from "components/Elements/Icons"
import { isNotEmpty } from "utils/validation"

export const DEFAULT_ACCEPTED_EXTENSIONS = ["xlsx", "xls"]
export const DEFAULT_MAX_FILE_SIZE = 10 // mo

export const InputXlsx = ({
    id,
    value,
    name,
    label = "",
    placeholder = i18next.t("file_placeholder", { ns: "form" }),
    disabled,
    readonly,
    // Styles
    style,
    className = "",
    classNameLabel = "",
    classNameInput = "",
    // Error management
    acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    required = false,
    customRules,
    showErrors,
    // Handler
    onChange,
    onValidate
}) => {
    const [selectedFileName, setSelectedFileName] = React.useState(value || null)
    const [fileError, setFileError] = React.useState(false)
    const [errors, setErrors] = React.useState([])
    const { alert } = useAlert()
    const { showModal } = useModal()
    const { t } = useTranslation()

    const rules = React.useMemo(() => [
        {
            key: "input-empty",
            check: (value) => required ? isNotEmpty(value) : true,
            message: t("error_mandatory_field", { ns: "form" })
        },
        ...(customRules || [])
    ], [required]) // eslint-disable-line
    // customRules in dependencies array trigger an infinite loop,
    // because custom rules include a check property defining a new function,
    // preventing useMemo to return a valid shallow-comparison
    // or event useDeepMemo to return a valid deep-comparison

    const validate = React.useCallback((currentValue) => {
        const newErrors = []
        rules.forEach(rule => {
            if (!rule.check(currentValue)) {
                newErrors.push({
                    origin: name,
                    rule: rule.key,
                    message: rule.message
                })
            }
        })
        setErrors(newErrors)
        onValidate(name, newErrors)
    }, [rules, name, onValidate])

    // validate on mount and each time value change
    React.useEffect(() => {
        validate(value)
        return () => {
            // On unmounted errors and validation are no longer needed
            setErrors([])
            if (onValidate) onValidate(name, [])
        }
    }, [value, validate, onValidate, name])

    const handleXlsxImport = async (file) => {
        try {
            const dataFromFile = await FileUtil.importXlsx(file)
            setFileError(false)
            setSelectedFileName(file.name)
            onChange(dataFromFile)
        } catch(error) {
            setFileError(true)
            console.error(error)
            alert("error", t("file_default_importation_error", { ns: "form" }))
        }
    }

    const handleChange = (event) => {
        const inputFileTarget = event.target
		const file = inputFileTarget.files[0]
        if (file) {
            try {
                // Forbidden extensions
                const fileExtension = file.name.split(".").pop()
                if (acceptedExtensions.indexOf(fileExtension) === -1) {
                    alert("error", t("file_extensions_file_error", { ns: "form", extension: fileExtension.toString() }))
                    setFileError(true)
                    return
                }
                // Oversized file
                if (file.size > maxFileSize * 1000000) {
                    alert("error", t("file_size_error", { ns: "form", maxSize: maxFileSize }))
                    setFileError(true)
                    return
                }

                showModal(
                    ImportConfirmationModal, {
                        confirmFileImport: handleXlsxImport,
                        event
                    },
                    { style: { maxWidth: "850px" } }
                )

            } catch(error) {
                alert("error", t("file_import_error", { ns: "form" }))
                throw new Error(error.toString())
            }
        }
    }

    return (
        <>
            <div className={"c-form-group " + (className)} style={style}>
                <label className={"c-label mb-1 " + classNameLabel}>{label} {required && "*"}</label>

                {readonly &&
                    <div>
                        {value &&
                            <a href={value.media_url} target="_blank" className={"c-btn c-btn-download mt-5 mb-5"} rel="noreferrer">
                                <DownloadIcon size={20} />
                                <span className="pl-2">{t("global.see")}</span>
                            </a>
                        }
                        {!value && disabled &&
                            <div className={"mt-7 mb-7"}>{t("file_no_files_imported_error", { ns: "form" })}</div>
                        }
                    </div>
                }

                { !disabled &&
                    <>
                        <input
                            type="file"
                            className="u-sr-only"
                            id={id}
                            name={name}
                            onChange={(e) => handleChange(e)}
                            onBlur={() => validate(value)}
                            disabled={disabled}
                            readOnly={readonly}
                            required={required}
                        />

                        <label className={`c-btn c-btn--primary ${disabled ? "is-disabled" : ""} ${classNameInput}`} htmlFor={id}>
                            <UploadIcon size={20}/>
                            <div className="pl-4">
                                {placeholder}
                            </div>
                        </label>
                        { selectedFileName && <p className="mt-5 italic">{t("file_last_filename_imported", { ns: "form", filename: selectedFileName })}</p> }
                    </>
                }
                { fileError && <p className="text-xs text-danger mt-2">{t("file_accepted_extensions_error", { ns: "form", extensions: acceptedExtensions.toString() })}</p>}

                {/* Error messages */}
                {showErrors && errors && errors.map((error, index) => {
                    return <p key={index} className="text-xs text-danger">{error.message}</p>
                })}
            </div>
        </>
    )
}

const ImportConfirmationModal = ({ confirmFileImport, event }) => {
    const { hideModal } = useModal()
    const { t } = useTranslation()
    const file = event.target?.files && [...event.target?.files][0]

    const confirmImport = async () => {
        hideModal()
        await confirmFileImport(file)
    }

    const cancelImport = () => {
        event.target.value = ""
        hideModal()
    }

    return (
        <div className="p-12">
            <h2 className="c-h2 text-primary text-xl font-bold text-center my-7">{t("create_modal_confirm_title", { ns: "bike" })}</h2>
            
            <Markdown
                specificComponents={{
                    p: ({ children }) => (
                        <p className="mb-5">{children}</p>
                    ),
                }}
            >
                {t("modal_validation_description", { ns: "bikeCollectionImport" })}
            </Markdown>

            <div className="flex justify-between flex-col md:flex-row-reverse mt-7">
                <button
                    type="button"
                    className="c-btn c-btn--primary px-7 mb-5 lg:m-0 uppercase"
                    onClick={confirmImport}
                >
                    {t("global.confirm")}
                </button>

                <button
                    type="button"
                    className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0 uppercase"
                    onClick={cancelImport}
                >
                    {t("global.cancel")}
                </button>
            </div>
        </div>
    )
  }